import React from 'react';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {
  Checkbox,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog,
  FormControlLabel,
  FormGroup,
  CircularProgress
} from '@mui/material';

import {
  getUserGroupList,
  updateUserGroups
} from '@shared/services/service-handlers/PrivateService';

import {APP} from 'src/styles/variables';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import FormConstants from '@shared/constants/FormConstants';
import {useAppContext} from '@context/AppContext';
import {LoadingButton} from '@mui/lab';

const UserGroupsAssigningDialog = (props: any): React.ReactElement => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const {onClose, open, selectedUser, ...other} = props;
  const [allUserGroups, setAllUserGroups]: any = React.useState([]);
  const [selectedUserGroupNamesList, setSelectedUserGroupNamesList]: any =
    React.useState([]);
  const [loaders, setLoaders] = React.useState({
    componentLoading: false,
    buttonLoading: false
  });
  const {
    appState: {applicationName}
  } = useAppContext();

  const init = async () => {
    try {
      setLoaders(prev => ({...prev, componentLoading: true}));

      const response = await getUserGroupList('', '', applicationName);
      const groups = response?.data?.rows ?? [];
      setAllUserGroups(groups);

      const userGroupsData =
        selectedUser?.row?.groups || selectedUser?.row?.group_names;

      if (userGroupsData) {
        const assignedGroups = userGroupsData
          .split(',')
          .map((g: string) => g.trim());

        const validGroups = assignedGroups.filter((name: string) =>
          groups.some((g: any) => g.name?.trim() === name?.trim())
        );
        setSelectedUserGroupNamesList(validGroups);
      }
      setLoaders(prev => ({...prev, componentLoading: false}));
    } catch (error) {
      setLoaders(prev => ({...prev, componentLoading: false}));
      enqueueSnackbar(getErrorMessage(error), SnackBarConfig.getError());
    }
  };

  React.useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () => {
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setLoaders(prevState => ({
        ...prevState,
        buttonLoading: true
      }));

      // eliminate none from groups
      const finalGroups = selectedUserGroupNamesList.filter(
        (item: any) => item !== FormConstants.USER_GROUP.NONE
      );

      const res = await updateUserGroups(
        selectedUser?.id,
        {
          group_names: finalGroups
        },
        applicationName
      );
      enqueueSnackbar(res?.message, SnackBarConfig.getSuccess());

      setLoaders(prevState => ({
        ...prevState,
        buttonLoading: false
      }));

      handleCancel();
    } catch (error) {
      setLoaders(prevState => ({
        ...prevState,
        buttonLoading: false
      }));
      const errMessage = getErrorMessage(error);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  const checkGroupExistence = (groupName: string): boolean => {
    return selectedUserGroupNamesList.includes(groupName);
  };

  const handleChange = (checkboxEvent: boolean, groupName: string) => {
    const isGroupAlreadyExisted = checkGroupExistence(groupName);

    if (checkboxEvent) {
      // if we check add the group name to selected group names list
      if (!isGroupAlreadyExisted) {
        const list = [...selectedUserGroupNamesList];
        list.push(groupName);
        setSelectedUserGroupNamesList(list);
      }
    } else {
      // if we uncheck remove the group name from selected group names list
      if (isGroupAlreadyExisted) {
        const list = selectedUserGroupNamesList.filter(
          (item: any) => item !== groupName
        );
        setSelectedUserGroupNamesList(list);
      }
    }
  };

  return (
    <Dialog
      sx={{'& .MuiDialog-paper': {width: '80%', maxHeight: 435}}}
      maxWidth="xs"
      open={open}
      {...other}>
      <DialogTitle
        sx={{
          fontWeight: '700',
          color: APP.PALETTE.APP_BAR_COLOR.INFO_CHIP,
          fontSize: '1rem !important'
        }}>
        {t('PRIVATE.USER_GROUPS.UPDATE_GROUPS', {ns: 'private'})}
      </DialogTitle>

      <DialogContent dividers>
        {loaders?.componentLoading ? (
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            <CircularProgress size={30} />
          </div>
        ) : (
          <FormGroup>
            {allUserGroups.map((option: any) => (
              <FormControlLabel
                key={option?.id}
                control={
                  <Checkbox
                    name={option?.name}
                    checked={checkGroupExistence(option?.name.trim())}
                    onChange={e => handleChange(e.target.checked, option.name)}
                  />
                }
                label={option?.name}
              />
            ))}
          </FormGroup>
        )}
      </DialogContent>

      <DialogActions>
        <LoadingButton
          type="submit"
          variant="outlined"
          sx={{
            mr: 1
          }}
          disabled={loaders.buttonLoading}
          onClick={handleCancel}>
          {t('PRIVATE.ROLES.CANCEL', {ns: 'private'})}
        </LoadingButton>
        <LoadingButton
          type="submit"
          loading={loaders.buttonLoading}
          variant="contained"
          sx={{
            mr: 1
          }}
          disabled={loaders.buttonLoading}
          onClick={handleSubmit}>
          {t('PRIVATE.ROLES.SAVE_ROLE', {ns: 'private'})}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
export default UserGroupsAssigningDialog;